// ============================================
// Manor Miser - Lobby Scene (native resolution)
// ============================================

import Phaser from 'phaser';
import { socketManager } from '../network/SocketManager';
import { UIButton } from '../ui/UIButton';
import { SoundManager } from '../audio/SoundManager';
import type { PlayerPublic } from '../shared/types';

export class LobbyScene extends Phaser.Scene {
  private players: PlayerPublic[] = [];
  private playerId: string = '';
  private hostId: string = '';
  private roomCode: string = '';
  private playerListContainer!: Phaser.GameObjects.Container;
  private startButton!: UIButton;
  private waitingText!: Phaser.GameObjects.BitmapText;
  private waitingDots: number = 0;

  constructor() {
    super({ key: 'LobbyScene' });
  }

  create(): void {
    const { width, height } = this.cameras.main;

    this.roomCode = this.registry.get('roomCode') || '';
    this.playerId = this.registry.get('playerId') || '';
    this.hostId = this.registry.get('hostId') || this.playerId;
    this.players = this.registry.get('players') || [];

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x1a1412, 1);
    bg.fillRect(0, 0, width, height);

    // Border
    const border = this.add.graphics();
    border.lineStyle(1, 0x4a3728);
    border.strokeRect(4, 4, width - 8, height - 8);

    // Corner accents
    border.lineStyle(1, 0xd4a855);
    const cs = 8;
    border.beginPath(); border.moveTo(4, 4 + cs); border.lineTo(4, 4); border.lineTo(4 + cs, 4); border.strokePath();
    border.beginPath(); border.moveTo(width - 4 - cs, 4); border.lineTo(width - 4, 4); border.lineTo(width - 4, 4 + cs); border.strokePath();
    border.beginPath(); border.moveTo(4, height - 4 - cs); border.lineTo(4, height - 4); border.lineTo(4 + cs, height - 4); border.strokePath();
    border.beginPath(); border.moveTo(width - 4 - cs, height - 4); border.lineTo(width - 4, height - 4); border.lineTo(width - 4, height - 4 - cs); border.strokePath();

    // Title
    const title = this.add.bitmapText(width / 2, 10, 'pixel', 'THE MANOR LOBBY');
    title.setOrigin(0.5, 0).setScale(2).setTint(0xd4a855);

    // Room code display
    const codeBox = this.add.graphics();
    codeBox.fillStyle(0x2d1f1a, 0.8);
    codeBox.fillRect(width / 2 - 36, 26, 72, 28);
    codeBox.lineStyle(1, 0xd4a855);
    codeBox.strokeRect(width / 2 - 36, 26, 72, 28);

    const codeLabel = this.add.bitmapText(width / 2, 28, 'pixel', 'ROOM CODE');
    codeLabel.setOrigin(0.5, 0).setScale(1).setTint(0x7a6a5a);

    const codeText = this.add.bitmapText(width / 2, 38, 'pixel', this.roomCode);
    codeText.setOrigin(0.5, 0).setScale(2).setTint(0xd4a855);
    codeText.setInteractive({ useHandCursor: true });
    codeText.on('pointerdown', () => {
      navigator.clipboard?.writeText(this.roomCode);
      const copied = this.add.bitmapText(width / 2, 56, 'pixel', 'COPIED!');
      copied.setOrigin(0.5, 0).setScale(1).setTint(0x6bff6b);
      this.tweens.add({
        targets: copied,
        alpha: 0,
        y: copied.y - 8,
        duration: 1000,
        onComplete: () => copied.destroy(),
      });
    });

    // Player list header
    const headerY = 60;
    const header = this.add.bitmapText(width / 2, headerY, 'pixel', 'VILLAGERS GATHERED');
    header.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855);

    // Player list container
    this.playerListContainer = this.add.container(width / 2, headerY + 10);

    // Waiting text
    this.waitingText = this.add.bitmapText(width / 2, height - 36, 'pixel', '');
    this.waitingText.setOrigin(0.5, 0).setScale(1).setTint(0x7a6a5a);

    this.updatePlayerList();

    // Animate waiting dots
    this.time.addEvent({
      delay: 500,
      callback: () => {
        this.waitingDots = (this.waitingDots + 1) % 4;
        this.updateWaitingText();
      },
      loop: true,
    });

    // Buttons
    const buttonY = height - 20;

    new UIButton(this, width / 2 - 40, buttonY, 'LEAVE', () => {
      socketManager.leaveRoom();
      this.cameras.main.fadeOut(300, 26, 20, 18);
      this.time.delayedCall(300, () => {
        this.scene.start('MenuScene');
      });
    }, 50, 16);

    this.startButton = new UIButton(this, width / 2 + 40, buttonY, 'START', () => {
      this.startButton.setDisabled(true);
      this.startButton.setText('WAIT...');
      socketManager.startGame();
    }, 60, 16);

    this.updateStartButton();
    this.setupSocketCallbacks();

    // Sound toggle
    const isMuted = SoundManager.muted;
    const soundBtn = this.add.bitmapText(width - 8, 8, 'pixel', isMuted ? 'M' : 'S');
    soundBtn.setOrigin(1, 0).setScale(1).setTint(0xd4a855).setAlpha(0.7);
    soundBtn.setInteractive({ useHandCursor: true });
    soundBtn.on('pointerover', () => soundBtn.setAlpha(1));
    soundBtn.on('pointerout', () => soundBtn.setAlpha(0.7));
    soundBtn.on('pointerdown', () => {
      const nowMuted = SoundManager.toggleMute();
      soundBtn.setText(nowMuted ? 'M' : 'S');
      if (!nowMuted) SoundManager.click();
    });

    this.cameras.main.fadeIn(300, 26, 20, 18);
  }

  private setupSocketCallbacks(): void {
    socketManager.setCallbacks({
      onPlayerJoined: (player) => {
        this.players.push(player);
        this.updatePlayerList();
        this.updateStartButton();
        SoundManager.playerJoined();
        this.showNotification(`${player.name.toUpperCase()} JOINED`, 0x6bff6b);
      },
      onPlayerLeft: (playerId, newHostId) => {
        const leftPlayer = this.players.find(p => p.id === playerId);
        this.players = this.players.filter((p) => p.id !== playerId);
        if (newHostId) {
          this.hostId = newHostId;
          this.players = this.players.map(p => ({
            ...p,
            isHost: p.id === newHostId,
          }));
        }
        this.updatePlayerList();
        this.updateStartButton();
        if (leftPlayer) {
          SoundManager.playerLeft();
          this.showNotification(`${leftPlayer.name.toUpperCase()} LEFT`, 0xffaa44);
        }
      },
      onPlayerKicked: (playerId) => {
        if (playerId === this.playerId) {
          this.showNotification('YOU WERE KICKED', 0xff6b6b);
          this.time.delayedCall(1500, () => {
            this.scene.start('MenuScene');
          });
        } else {
          const kickedPlayer = this.players.find(p => p.id === playerId);
          this.players = this.players.filter((p) => p.id !== playerId);
          this.updatePlayerList();
          this.updateStartButton();
          if (kickedPlayer) {
            this.showNotification(`${kickedPlayer.name.toUpperCase()} KICKED`, 0xff6b6b);
          }
        }
      },
      onHostChanged: (newHostId) => {
        this.hostId = newHostId;
        this.players = this.players.map(p => ({
          ...p,
          isHost: p.id === newHostId,
        }));
        this.updatePlayerList();
        this.updateStartButton();
        if (newHostId === this.playerId) {
          this.showNotification('YOU ARE NOW HOST', 0xd4a855);
        }
      },
      onGameStarted: (gameState, yourMoney) => {
        this.registry.set('gameState', gameState);
        this.registry.set('yourMoney', yourMoney);
        this.cameras.main.fadeOut(500, 26, 20, 18);
        this.time.delayedCall(500, () => {
          this.scene.start('GameScene');
        });
      },
      onError: (message) => {
        console.error('[Lobby] Error:', message);
        this.showNotification(message.toUpperCase(), 0xff6b6b);
        this.startButton.setDisabled(false);
        this.startButton.setText('START');
      },
    });
  }

  private showNotification(text: string, color: number): void {
    const { width } = this.cameras.main;

    const notification = this.add.bitmapText(width / 2, 56, 'pixel', text);
    notification.setOrigin(0.5, 0).setScale(1).setTint(color).setAlpha(0);

    this.tweens.add({
      targets: notification,
      alpha: 1,
      y: notification.y - 6,
      duration: 300,
      onComplete: () => {
        this.time.delayedCall(1500, () => {
          this.tweens.add({
            targets: notification,
            alpha: 0,
            duration: 300,
            onComplete: () => notification.destroy(),
          });
        });
      },
    });
  }

  private updatePlayerList(): void {
    this.playerListContainer.removeAll(true);

    const spacing = 10;
    const maxVisible = 8;

    const visiblePlayers = this.players.slice(0, maxVisible);

    visiblePlayers.forEach((player, index) => {
      const y = index * spacing;
      const isHost = player.id === this.hostId;
      const isMe = player.id === this.playerId;

      // Player name
      const displayName = player.name.length > 14 ? player.name.substring(0, 13) + '.' : player.name;
      const nameText = this.add.bitmapText(-60, y, 'pixel', displayName.toUpperCase());
      nameText.setOrigin(0, 0).setScale(1);
      nameText.setTint(isMe ? 0xd4a855 : 0xf5e6d3);
      this.playerListContainer.add(nameText);

      // Host indicator
      if (isHost) {
        const hostLabel = this.add.bitmapText(60, y, 'pixel', 'HOST');
        hostLabel.setOrigin(1, 0).setScale(1).setTint(0xd4a855);
        this.playerListContainer.add(hostLabel);
      }

      // Kick button (host only, can't kick self)
      if (this.playerId === this.hostId && !isMe) {
        const kickBtn = this.add.bitmapText(70, y, 'pixel', 'X');
        kickBtn.setOrigin(0.5, 0).setScale(1).setTint(0x7a5050);
        kickBtn.setInteractive({ useHandCursor: true });
        kickBtn.on('pointerdown', () => socketManager.kickPlayer(player.id));
        kickBtn.on('pointerover', () => kickBtn.setTint(0xff6b6b));
        kickBtn.on('pointerout', () => kickBtn.setTint(0x7a5050));
        this.playerListContainer.add(kickBtn);
      }
    });

    if (this.players.length > maxVisible) {
      const moreText = this.add.bitmapText(0, maxVisible * spacing, 'pixel',
        `+${this.players.length - maxVisible} MORE`);
      moreText.setOrigin(0.5, 0).setScale(1).setTint(0x7a6a5a);
      this.playerListContainer.add(moreText);
    }

    this.updateWaitingText();
  }

  private updateWaitingText(): void {
    if (!this.waitingText) return;

    if (this.players.length < 2) {
      const dots = '.'.repeat(this.waitingDots);
      this.waitingText.setText(`WAITING FOR PLAYERS${dots}`);
    } else if (this.playerId !== this.hostId) {
      const dots = '.'.repeat(this.waitingDots);
      this.waitingText.setText(`WAITING FOR HOST${dots}`);
    } else {
      this.waitingText.setText('PRESS START WHEN READY');
    }
  }

  private updateStartButton(): void {
    const isHost = this.playerId === this.hostId;
    const hasEnoughPlayers = this.players.length >= 2;

    this.startButton.setDisabled(!isHost || !hasEnoughPlayers);

    if (!isHost) {
      this.startButton.setText('WAIT...');
    } else if (!hasEnoughPlayers) {
      this.startButton.setText('NEED 2+');
    } else {
      this.startButton.setText('START');
    }
  }
}
